import React, { useEffect, useState } from 'react';
import { Text } from '../../../../ui';
import FilterHotels from '../../../../components/FilterHotels';
import Search from '../../../../components/Search';
import {
  KoobButton,
  KoobDataTable,
  KoobInputNumber,
  KoobInputStars,
  KoobTableAction,
  KoobId,
} from '@koob/ui';
import { Stack } from '@koob/margaret';
import { useApp, useSearchParams, useSnack } from '../../../../hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDebounce, useDeepCompareEffect } from 'react-use';
import { useAsync } from 'react-async';
import { getHotels } from 'api/node/hotels';
import { isEqual } from 'lodash';
import { OrderColumnEnum, OrderDirectionEnum } from '@koob/enums';
import { decodeId } from 'utils';
import ProductStateBadge from './ProductStateBadge';
import {
  KoobLocationSearch,
  SEARCH_DEFAULT_TYPES,
  SEARCH_TYPES,
} from '@koob/search';

const LOCATION_SEARCH_TYPES = [...SEARCH_DEFAULT_TYPES, SEARCH_TYPES.HOTEL];

export default function HotelsBrowser({
  hotels,
  setHotels,
  isHotelSelected,
  selectAllShownHotels,
  onAddHotel,
  globalMargin,
  setGlobalMargin,
  isGlobalMarginPercent,
  setIsGlobalMarginPercent,
}) {
  const { t, i18n } = useTranslation('toConnectionRequest');
  const { currentOrganizationId, selectedDistributionOrganization } = useApp();
  const { requestId } = useParams();
  const [filters, setFilters] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [{ kindsIds, stars }] = useSearchParams();
  const { notify } = useSnack();
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const dmcOrganizationId =
    selectedDistributionOrganization?.value ?? currentOrganizationId;
  const limitPerPage = 50;

  useDebounce(
    () => {
      setDebouncedFilters(filters);
    },
    500,
    [filters],
  );

  const requestFilters = {
    stars: Boolean(stars) ? Number(stars) : undefined,
    // KD-3547 — no state filter: a DMC distributes hotels it is still
    // preparing (in progress / stand by), not only the available ones.
    kindsIds: Boolean(kindsIds)
      ? (kindsIds ?? []).map(({ value }) => value)
      : undefined,
    dmcId: decodeId(dmcOrganizationId),
    discarded: false,
    perPage: limitPerPage,
    page: page,
    ...debouncedFilters,
    direction: OrderDirectionEnum.DESC,
    order: OrderColumnEnum.UPDATED_AT,
    toconnectionrequestId: requestId,
  };

  const {
    data: hotelList,
    isLoading,
    reload,
  } = useAsync({
    promiseFn: getHotels,
    filters: requestFilters,
    locale: 'en',
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const handleLoadNextPage = () => {
    setPage((page) => page + 1);
  };

  // Keyed on the debounced filters: the raw ones change on every keystroke of
  // the search box, and the request reads the debounced copy anyway.
  useDeepCompareEffect(() => {
    setPage(1);
    setHasNextPage(true);
    reload();
  }, [debouncedFilters]);

  // Keyed on the resolved response, so an empty result still runs: a search
  // with no match clears the list instead of leaving the previous results.
  useEffect(() => {
    const data = hotelList?.data?.data;
    if (!data) {
      return;
    }
    const formattedHotels = data.map((hotel) => ({
      id: hotel?.id.toString(),
      displayName: hotel?.displayName,
      state: hotel?.state,
      country: hotel?.alpha2,
      city: hotel?.region,
      stars: hotel.stars,
      margin: null,
      isMarginPercent: null,
    }));
    if (page === 1) {
      setHotels(formattedHotels);
    } else {
      setHotels((prevHotels) => [...prevHotels, ...formattedHotels]);
    }
    setHasNextPage(data.length === limitPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelList, setHotels]);

  const columns = [
    {
      key: 'displayName',
      label: t('hotelName'),
      width: '100px',
      render: (hotel) => (
        <div className="flex flex-col items-start">
          <span className="font-medium">{hotel?.displayName}</span>
          <KoobId id={hotel?.id} />
        </div>
      ),
    },
    {
      key: 'country',
      label: t('countryName'),
      width: '60px',
      render: (hotel) => hotel?.country,
    },
    {
      key: 'city',
      label: t('cityName'),
      width: '100px',
      render: (hotel) => hotel?.city,
    },
    {
      key: 'stars',
      label: t('starsName'),
      width: '50px',
      render: (hotel) => <KoobInputStars value={hotel?.stars} />,
    },
    {
      key: 'state',
      label: t('state'),
      width: '70px',
      render: (hotel) => <ProductStateBadge state={hotel?.state} />,
    },
    {
      key: 'selected',
      label: t('enabled'),
      width: '40px',
      render: (hotel) => (
        <KoobTableAction
          icon={
            <i
              className={`fa-regular fa-eye ${
                isHotelSelected(hotel?.id) ? 'text-orange-500' : ''
              }`}
            />
          }
          label={t('enabled')}
          onClick={() => {
            onAddHotel(hotel);
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <Text type="h3">{t('browseHotels')}</Text>

      <div className="my-4 rounded-2xl border border-solid border-k-gray bg-white p-5">
        <div className="mb-2 flex justify-between">
          <h6 className="font-medium">
            {t('globalMargin', { unit: isGlobalMarginPercent ? '(%)' : '' })}
          </h6>

          <div className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={isGlobalMarginPercent}
              onChange={() => setIsGlobalMarginPercent(!isGlobalMarginPercent)}
            />
            <p className="text-sm">{t('usePercentMargin')}</p>
          </div>
        </div>

        <KoobInputNumber
          min={0}
          value={globalMargin}
          emptyValue={0}
          onValueChange={(value) => setGlobalMargin(value ?? 0)}
        />
      </div>

      <KoobDataTable
        search={
          <Search
            name="search"
            placeholder={t('misc:searchByNameOrId')}
            value={filters.search ?? ''}
            onChange={(value) =>
              setFilters((current) => ({ ...current, search: value }))
            }
          />
        }
        filters={
          <div className="w-72">
            <KoobLocationSearch
              placeholder={t('locationsSearch', { ns: 'hotel' })}
              value={selectedLocation}
              onValueChange={(value) => {
                setSelectedLocation(value);
                // The picker owns the location keys only; the typed search
                // survives a change of place.
                setFilters(({ search }) => ({
                  search,
                  ...(value?.kind && { [value.kind]: [value.id] }),
                }));
              }}
              types={LOCATION_SEARCH_TYPES}
              locale={i18n.language}
              fromOrganizationId={dmcOrganizationId}
              pluralKinds
              // KD-3547 — match the grid: suggest hotels of every state.
              includeUnavailable
            />
          </div>
        }
        toolbarActions={
          <>
            <KoobButton
              variant="secondary"
              aria-label={t('misc:select')}
              onClick={() => {
                if (window.confirm(t('selectAllHotelConfirmationMessage'))) {
                  selectAllShownHotels(hotels);
                  notify(t('selectedAllHotelsuccess'));
                }
              }}
            >
              <i className="fa-light fa-eye" />
            </KoobButton>
            <FilterHotels showLocation={false} showKind={false} />
          </>
        }
        columns={columns}
        data={hotels || []}
        loading={isLoading && (hotels || []).length === 0}
      />

      {hasNextPage && (
        <Stack alignX="center" className="mt-4">
          <KoobButton variant="secondary" onClick={handleLoadNextPage}>
            {t('misc:seeMore')}
          </KoobButton>
        </Stack>
      )}
    </div>
  );
}
